// ============================================================================
// 정통 실사 오디오 & 성우 보이스 전용 사운드 엔진 (100% Real Audio Engine)
// - G:\내 드라이브\플랫폼\사운드\_최종본_검수세트_FINAL 에셋 100% 전용 (합성 비프음 전면 제거)
//   1) 1_내목소리_성우변환_핵심보이스_11종 + 2_전체_단문보이스_24종 (/assets/audio/voice/*.mp3)
//   2) 3_최종채택_핵심타격음_및_SFX 18종 (/assets/audio/sfx/*.mp3)
// ============================================================================

import { PieceType } from '../core/JanggiRules';

export type SfxTrackKey =
  | 'sfx_piece_select'
  | 'sfx_piece_move_normal'
  | 'sfx_piece_move_heavy'
  | 'sfx_clack_zelkova'
  | 'sfx_clack_jade'
  | 'sfx_clack_ebony_gold'
  | 'sfx_piece_capture_normal'
  | 'sfx_piece_capture_major'
  | 'sfx_check_gong'
  | 'sfx_check_impact'
  | 'sfx_bikjang_alert'
  | 'sfx_checkmate_finish'
  | 'sfx_match_start_gong'
  | 'sfx_sangsu_activate'
  | 'sfx_masu_activate'
  | 'sfx_byoyomi_tick'
  | 'sfx_win_fanfare'
  | 'sfx_lose_sigh';

export type VoiceTrackKey =
  | 'vo_janggun_01'
  | 'vo_janggun_02'
  | 'vo_janggun_03'
  | 'vo_janggun_cha'
  | 'vo_janggun_po'
  | 'vo_janggun_ma'
  | 'vo_janggun_sang'
  | 'vo_janggun_yang'
  | 'vo_meonggun_01'
  | 'vo_meonggun_02'
  | 'vo_meonggun_03'
  | 'vo_bikjang'
  | 'vo_checkmate_01'
  | 'vo_checkmate_02'
  | 'vo_call_sangsu'
  | 'vo_call_hunsu'
  | 'vo_call_myosu'
  | 'vo_danger_01'
  | 'vo_danger_02'
  | 'vo_pass_turn'
  | 'vo_byoyomi_start'
  | 'vo_count_54321'
  | 'vo_match_start'
  | 'vo_match_win';

const ALL_SFX_KEYS: SfxTrackKey[] = [
  'sfx_piece_select',
  'sfx_piece_move_normal',
  'sfx_piece_move_heavy',
  'sfx_clack_zelkova',
  'sfx_clack_jade',
  'sfx_clack_ebony_gold',
  'sfx_piece_capture_normal',
  'sfx_piece_capture_major',
  'sfx_check_gong',
  'sfx_check_impact',
  'sfx_bikjang_alert',
  'sfx_checkmate_finish',
  'sfx_match_start_gong',
  'sfx_sangsu_activate',
  'sfx_masu_activate',
  'sfx_byoyomi_tick',
  'sfx_win_fanfare',
  'sfx_lose_sigh',
];

const ALL_VOICE_KEYS: VoiceTrackKey[] = [
  'vo_janggun_01',
  'vo_janggun_02',
  'vo_janggun_03',
  'vo_janggun_cha',
  'vo_janggun_po',
  'vo_janggun_ma',
  'vo_janggun_sang',
  'vo_janggun_yang',
  'vo_meonggun_01',
  'vo_meonggun_02',
  'vo_meonggun_03',
  'vo_bikjang',
  'vo_checkmate_01',
  'vo_checkmate_02',
  'vo_call_sangsu',
  'vo_call_hunsu',
  'vo_call_myosu',
  'vo_danger_01',
  'vo_danger_02',
  'vo_pass_turn',
  'vo_byoyomi_start',
  'vo_count_54321',
  'vo_match_start',
  'vo_match_win',
];

export class SoundEngine {
  public enabled: boolean = true;
  private sfxCache: Map<SfxTrackKey, HTMLAudioElement> = new Map();
  private voiceCache: Map<VoiceTrackKey, HTMLAudioElement> = new Map();
  private currentVoice: HTMLAudioElement | null = null;

  private janggunCycle: number = 0;
  private meonggunCycle: number = 0;
  private dangerCycle: number = 0;
  private checkmateCycle: number = 0;

  constructor() {
    if (typeof window !== 'undefined' && typeof Audio !== 'undefined') {
      for (const key of ALL_SFX_KEYS) {
        const audio = new Audio(`/assets/audio/sfx/${key}.mp3`);
        audio.preload = 'auto';
        this.sfxCache.set(key, audio);
      }
      for (const key of ALL_VOICE_KEYS) {
        const audio = new Audio(`/assets/audio/voice/${key}.mp3`);
        audio.preload = 'auto';
        this.voiceCache.set(key, audio);
      }
    }
  }

  public toggle(): boolean {
    this.enabled = !this.enabled;
    if (!this.enabled && this.currentVoice) {
      this.currentVoice.pause();
      this.currentVoice.currentTime = 0;
      this.currentVoice = null;
    }
    return this.enabled;
  }

  public setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    if (!this.enabled && this.currentVoice) {
      this.currentVoice.pause();
      this.currentVoice.currentTime = 0;
      this.currentVoice = null;
    }
  }

  public isEnabled(): boolean {
    return this.enabled;
  }

  public setMuted(muted: boolean): void {
    this.setEnabled(!muted);
  }

  public unlockAudio(): void {
    // HTML5 Audio elements are unlocked on user gesture automatically
  }

  public playSfx(key: SfxTrackKey, volume: number = 0.9): void {
    if (!this.enabled || typeof window === 'undefined') return;
    try {
      const base = this.sfxCache.get(key);
      const clip = base ? (base.cloneNode(true) as HTMLAudioElement) : new Audio(`/assets/audio/sfx/${key}.mp3`);
      clip.volume = Math.max(0, Math.min(1, volume));
      clip.play().catch(() => {});
    } catch {
      // 브라우저 자동재생 정책 예외 무시
    }
  }

  public playVoice(key: VoiceTrackKey, volume: number = 1.0, delayMs: number = 0): void {
    if (!this.enabled || typeof window === 'undefined') return;
    const run = () => {
      if (!this.enabled) return;
      try {
        if (this.currentVoice) {
          this.currentVoice.pause();
          this.currentVoice.currentTime = 0;
        }
        const base = this.voiceCache.get(key);
        const clip = base ? (base.cloneNode(true) as HTMLAudioElement) : new Audio(`/assets/audio/voice/${key}.mp3`);
        clip.volume = Math.max(0, Math.min(1, volume));
        this.currentVoice = clip;
        clip.play().catch(() => {});
      } catch {
        // 브라우저 자동재생 정책 예외 무시
      }
    };

    if (delayMs > 0) {
      setTimeout(run, delayMs);
    } else {
      run();
    }
  }

  // 1. 기물 선택음
  public playPieceSelect(): void {
    this.playSfx('sfx_piece_select', 0.85);
  }

  // 2. 장기판 재질 & 기물 무게별 착수 타격음 ("딱!")
  public playPieceClack(
    volumeMultiplier: number = 1,
    pieceType?: PieceType,
    boardSkinId?: string,
    pieceSkinId?: string
  ): void {
    const vol = Math.min(1, 0.9 * volumeMultiplier);

    if (
      boardSkinId === 'imperial_jade' ||
      boardSkinId === 'board_jade_celadon' ||
      boardSkinId === 'board_jade_palace' ||
      pieceSkinId === 'white_jade' ||
      pieceSkinId === 'piece_jade_royal'
    ) {
      this.playSfx('sfx_clack_jade', vol);
      return;
    }
    if (
      boardSkinId === 'royal_ebony' ||
      boardSkinId === 'board_ebony_gold' ||
      boardSkinId === 'royal_pine' ||
      boardSkinId === 'board_royal_pine' ||
      boardSkinId === 'board_imperial_dragon' ||
      boardSkinId === 'board_three_kingdoms_stone' ||
      pieceSkinId === 'royal_gold_obsidian' ||
      pieceSkinId === 'piece_obsidian_gold' ||
      pieceSkinId === 'piece_war_drum' ||
      pieceSkinId === 'piece_mother_of_pearl'
    ) {
      this.playSfx('sfx_clack_ebony_gold', vol);
      return;
    }
    if (pieceType === 'cha' || pieceType === 'po' || pieceType === 'king') {
      this.playSfx('sfx_piece_move_heavy', vol);
      return;
    }
    if (
      boardSkinId === 'classic_kaya' ||
      boardSkinId === 'board_zelkova' ||
      boardSkinId === 'red_sandalwood' ||
      boardSkinId === 'board_rosewood_lacquer' ||
      boardSkinId === 'board_rosewood' ||
      pieceSkinId === 'piece_jujube_classic' ||
      pieceSkinId === 'piece_boxwood_master'
    ) {
      this.playSfx('sfx_clack_zelkova', vol);
      return;
    }
    this.playSfx('sfx_piece_move_normal', vol);
  }

  // 3. 계급별 기물 포획 타격음 (대물 포획 vs 일반 포획 + 외통 피니시)
  public playTieredCaptureImpact(capturedType: PieceType): void {
    if (capturedType === 'king') {
      this.playSfx('sfx_checkmate_finish', 1.0);
      const mateKey: VoiceTrackKey = this.checkmateCycle++ % 2 === 0 ? 'vo_checkmate_01' : 'vo_checkmate_02';
      this.playVoice(mateKey, 1.0, 90);
      return;
    }

    if (capturedType === 'cha' || capturedType === 'po' || capturedType === 'sa') {
      this.playSfx('sfx_piece_capture_major', 1.0);
      if (capturedType === 'cha' || capturedType === 'po') {
        this.playVoice('vo_call_myosu', 0.95, 110);
      }
      return;
    }

    this.playSfx('sfx_piece_capture_normal', 0.92);
  }

  public playPieceCapture(): void {
    this.playSfx('sfx_piece_capture_normal', 0.92);
  }

  // 4. 대국 개시 징 + 성우 "대국 개시" 콜
  public playMatchStart(): void {
    this.playSfx('sfx_match_start_gong', 0.92);
    this.playVoice('vo_match_start', 1.0, 140);
  }

  // 5. 장군(將軍) 징 & 임팩트 + 기물별/양장군/성우 장군 보이스 콜
  public playCheckAlert(checkingPieceType?: PieceType, isDoubleCheck: boolean = false): void {
    this.playSfx('sfx_check_impact', 0.95);
    this.playSfx('sfx_check_gong', 0.85);

    if (isDoubleCheck) {
      this.playVoice('vo_janggun_yang', 1.0, 80);
      return;
    }

    // 내 목소리 성우변환 핵심보이스(vo_janggun_01, vo_janggun_02)와 기물별 장군 콜을 교차 재생
    const idx = this.janggunCycle++;
    if (idx % 2 === 0) {
      const coreKey: VoiceTrackKey = idx % 4 === 0 ? 'vo_janggun_01' : 'vo_janggun_02';
      this.playVoice(coreKey, 1.0, 70);
      return;
    }

    if (checkingPieceType === 'cha') {
      this.playVoice('vo_janggun_cha', 1.0, 70);
    } else if (checkingPieceType === 'po') {
      this.playVoice('vo_janggun_po', 1.0, 70);
    } else if (checkingPieceType === 'ma') {
      this.playVoice('vo_janggun_ma', 1.0, 70);
    } else if (checkingPieceType === 'sang') {
      this.playVoice('vo_janggun_sang', 1.0, 70);
    } else {
      this.playVoice('vo_janggun_03', 1.0, 70);
    }
  }

  // 6. 멍군(防軍) 수비 성공 시 성우 보이스 콜 (핵심보이스 vo_meonggun_02, vo_meonggun_03 우선)
  public playMeonggunAlert(): void {
    this.playSfx('sfx_piece_move_heavy', 0.9);
    const list: VoiceTrackKey[] = ['vo_meonggun_02', 'vo_meonggun_03', 'vo_meonggun_01'];
    const key = list[this.meonggunCycle++ % list.length];
    this.playVoice(key, 1.0, 60);
  }

  // 7. 빅장(왕과 왕 마주봄) 알림음 + 성우 보이스 콜
  public playBikjangAlert(): void {
    this.playSfx('sfx_bikjang_alert', 0.95);
    this.playVoice('vo_bikjang', 1.0, 80);
  }

  // 8. 호신강기 위기감지 방지권 자동 발동 사운드 + 위기 경고 성우 보이스 (vo_danger_02 / vo_danger_01)
  public playCrisisShieldTrigger(): void {
    this.playSfx('sfx_byoyomi_tick', 0.9);
    this.playSfx('sfx_masu_activate', 0.85);
    const key: VoiceTrackKey = this.dangerCycle++ % 2 === 0 ? 'vo_danger_02' : 'vo_danger_01';
    this.playVoice(key, 1.0, 80);
  }

  // 9. 상수(象手) 비급 발동음 + 성우 보이스 (vo_call_sangsu)
  public playSangsuActivate(): void {
    this.playSfx('sfx_sangsu_activate', 0.95);
    this.playVoice('vo_call_sangsu', 1.0, 70);
  }

  // 10. 훈수(訓手) 비급 발동음 + 성우 보이스 (vo_call_hunsu)
  public playHunsuActivate(): void {
    this.playSfx('sfx_masu_activate', 0.95);
    this.playVoice('vo_call_hunsu', 1.0, 70);
  }

  // 11. 천안통(天眼通) / 묘수 발동음 + 성우 보이스 (vo_call_myosu)
  public playMyosuActivate(): void {
    this.playSfx('sfx_masu_activate', 0.95);
    this.playVoice('vo_call_myosu', 1.0, 70);
  }

  // 하위 호환용 비급 발동 호출
  public playVisionActivate(): void {
    this.playSfx('sfx_sangsu_activate', 0.92);
  }

  // 12. 한 수 쉼 (턴 넘김) 성우 보이스 (vo_pass_turn)
  public playPassTurn(): void {
    this.playSfx('sfx_piece_select', 0.85);
    this.playVoice('vo_pass_turn', 1.0, 40);
  }

  // 13. 초읽기 진입 및 5·4·3·2·1 카운트다운 보이스
  public playByoyomiStart(): void {
    this.playSfx('sfx_byoyomi_tick', 0.9);
    this.playVoice('vo_byoyomi_start', 1.0, 50);
  }

  public playByoyomiCountdown(): void {
    this.playSfx('sfx_byoyomi_tick', 0.95);
    this.playVoice('vo_count_54321', 1.0, 40);
  }

  // 14. 감정표현 스티커 클릭 시 (기분별 실사 효과음 매핑)
  public playEmotePop(type?: 'laugh' | 'tease' | 'angry' | 'cry' | 'praise' | string): void {
    this.playEmoteSound(type);
  }

  public playEmoteSound(type?: 'laugh' | 'tease' | 'angry' | 'cry' | 'praise' | string): void {
    if (type === 'angry') {
      this.playSfx('sfx_check_impact', 0.78);
    } else if (type === 'praise' || type === 'laugh') {
      this.playSfx('sfx_sangsu_activate', 0.8);
    } else if (type === 'cry') {
      this.playSfx('sfx_lose_sigh', 0.75);
    } else {
      this.playSfx('sfx_piece_select', 0.85);
    }
  }

  // 15. 대국 승리 팡파르 + 승리 성우 보이스 (vo_match_win)
  public playVictoryFanfare(withVoice: boolean = false): void {
    this.playSfx('sfx_win_fanfare', 0.95);
    if (withVoice) {
      this.playVoice('vo_match_win', 1.0, 180);
    }
  }

  // 16. 대국 패배 한숨/북 울림 (sfx_lose_sigh)
  public playDefeatDrum(): void {
    this.playSfx('sfx_lose_sigh', 0.95);
  }
}
